import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Send, MessageSquare, ArrowLeft, Loader2 } from "lucide-react";

export function Messages() {
  const { user } = useAuth();
  const [convs, setConvs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!user || user === false) return;
    api.get("/conversations").then((r) => { setConvs(r.data); setLoading(false); });
  }, [user]);

  if (!user || user === false) return <div className="max-w-lg mx-auto py-20 text-center"><Link to="/login" className="tl-btn-primary">Login to view messages</Link></div>;

  return (
    <div data-testid="messages-page" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6"><div className="tl-label mb-1">Inbox</div><h1 className="text-3xl font-display font-bold">Messages</h1></div>
      {loading ? <div className="text-center py-20 text-[#475569]"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div> :
        convs.length === 0 ? (
          <div data-testid="no-conversations" className="tl-card p-10 text-center">
            <MessageSquare className="w-8 h-8 mx-auto mb-3 text-[#64748B]" />
            <div className="font-display font-semibold mb-1">No conversations yet</div>
            <div className="text-sm text-[#475569]">Contact an expert or wait for enquiries to start a thread.</div>
          </div>
        ) : (
          <div className="space-y-2">
            {convs.map((c) => (
              <Link key={c.id} to={`/messages/${c.id}`} data-testid={`conv-${c.id}`} className="tl-card p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#2563EB] to-[#0891B2] flex items-center justify-center text-sm font-semibold">
                  {(user.id === c.customer_id ? c.target_name : c.customer_name)?.[0]?.toUpperCase() || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold truncate">{user.id === c.customer_id ? c.target_name : c.customer_name}</span>
                    {c.unread && <span data-testid={`unread-badge-${c.id}`} className="w-2 h-2 rounded-full bg-[#0891B2]" />}
                  </div>
                  <div className="text-xs text-[#475569] truncate">{c.project_title}</div>
                  <div className="text-xs text-[#64748B] truncate mt-0.5">{c.last_message}</div>
                </div>
                <div className="text-xs text-[#64748B] shrink-0">{new Date(c.last_message_at).toLocaleDateString()}</div>
              </Link>
            ))}
          </div>
        )}
    </div>
  );
}

export function Conversation() {
  const { cid } = useParams();
  const { user } = useAuth();
  const [conv, setConv] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef();

  const load = () => api.get(`/conversations/${cid}`).then((r) => { setConv(r.data.conversation); setMsgs(r.data.messages); });
  useEffect(() => { if (user && user !== false) load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [cid, user]); // eslint-disable-line
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [msgs.length]);

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setSending(true);
    try {
      await api.post(`/conversations/${cid}/messages`, { body });
      setBody(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  if (!user || user === false) return null;
  if (!conv) return <div className="max-w-3xl mx-auto py-20 text-center text-[#475569]"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;

  const otherName = user.id === conv.customer_id ? conv.target_name : conv.customer_name;

  return (
    <div data-testid="conversation-page" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col" style={{ height: "calc(100vh - 64px)" }}>
      <Link to="/messages" className="flex items-center gap-2 text-sm text-[#475569] hover:text-slate-900 mb-4 shrink-0"><ArrowLeft className="w-4 h-4" /> Back to messages</Link>
      <div className="tl-card p-4 mb-3 shrink-0">
        <div className="font-display font-semibold">{otherName}</div>
        <div className="text-xs text-[#475569]">{conv.project_title}</div>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-3 py-2 pr-1">
        {msgs.map((m) => {
          const mine = m.sender_id === user.id;
          return (
            <div key={m.id} data-testid={`msg-${m.id}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[70%] rounded-2xl px-4 py-2 ${mine ? "bg-[#2563EB] text-slate-900" : "bg-[#F8FAFC] border border-slate-200"}`}>
                {!mine && <div className="text-[10px] text-[#0891B2] mb-1 font-semibold">{m.sender_name}</div>}
                <div className="text-sm whitespace-pre-wrap">{m.body}</div>
                <div className="text-[10px] opacity-60 mt-1">{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
              </div>
            </div>
          );
        })}
      </div>
      <form onSubmit={submit} className="flex gap-2 mt-3 shrink-0">
        <input data-testid="msg-input" value={body} onChange={(e) => setBody(e.target.value)}
          placeholder="Type a message…" className="tl-input flex-1" />
        <button data-testid="msg-send" disabled={sending || !body.trim()} className="tl-btn-primary"><Send className="w-4 h-4" /></button>
      </form>
    </div>
  );
}
